import React, { useEffect, useMemo, useState } from "react";
import { Upload, Layers, FolderTree, Tag, Package, ArrowRight, SlidersHorizontal, X, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getBrands, getProducts, getProductsByCategory, getSimilarList } from "@/lib/api";
import { num } from "@/lib/calc";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const selCls =
  "rounded-none border-border bg-background font-mono text-sm h-11 focus:ring-1 focus:ring-primary";

// Dynamic technical filters per top-level category. Each toggle matches a product's specs.
const hasS = (v, re) => re.test(String(v ?? ""));
const FILTER_DEFS = {
  posa_piastrelle: {
    thickness: true,
    toggles: [
      { key: "interno", labelKey: "fInterno", match: (s) => s.interno === true },
      { key: "esterno", labelKey: "fEsterno", match: (s) => s.esterno === true },
      { key: "classe", labelKey: "fClasse", match: (s) => hasS(s.resistenza, /S1|S2/i) },
      { key: "rapida", labelKey: "fRapido", match: (s) => s.rapida === true },
    ],
  },
  preparazione_fondo: {
    thickness: true,
    toggles: [
      { key: "pompabile", labelKey: "fPompabile", match: (s) => s.pompabile === true },
      { key: "rapida", labelKey: "fRapidaAsc", match: (s) => s.rapida === true },
    ],
  },
  impermeabilizzazione: {
    thickness: false,
    toggles: [
      { key: "uv", labelKey: "fUV", match: (s) => s.esterno === true },
      { key: "bicomp", labelKey: "fBicomp", match: (s) => hasS(`${s.tipologia} ${s.base_chimica}`, /bicomponente|two-?component/i) },
      { key: "flex", labelKey: "fFlex", match: (s) => !!s.comportamento_elastico || hasS(s.tipologia, /elastic|flessib|flexible/i) },
    ],
  },
};

export default function ProductPicker({ side, categories, value, onSelect, onOpenUpload, refreshKey, lockCategory, lockSubcategory, onContextChange, enableFilters, refProductId, onSuggested }) {
  const { t, tc } = useApp();
  const enable = !!enableFilters;
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [brand, setBrand] = useState("");
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [catProducts, setCatProducts] = useState([]);
  const [active, setActive] = useState({});       // { toggleKey: true }
  const [thickness, setThickness] = useState("");
  const [suggestedIds, setSuggestedIds] = useState(new Set());
  const [orderIds, setOrderIds] = useState([]);

  const isLocked = lockCategory !== undefined;
  const subs = categories.find((c) => c.id === category)?.subcategories || [];

  useEffect(() => {
    if (!isLocked) return;
    setCategory(lockCategory || "");
    setSubcategory("");
    setBrand("");
    onSelect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockCategory]);

  useEffect(() => {
    if (onContextChange) onContextChange({ category, subcategory });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subcategory]);

  // Filter mode: load ALL products of the selected category at once
  useEffect(() => {
    if (!enable) return;
    if (!category) { setCatProducts([]); return; }
    getProductsByCategory(category).then(setCatProducts).catch(() => setCatProducts([]));
  }, [enable, category, refreshKey]);

  useEffect(() => {
    if (enable) return;
    if (!subcategory) { setBrands([]); return; }
    getBrands(subcategory).then(setBrands).catch(() => setBrands([]));
  }, [enable, subcategory, refreshKey]);

  useEffect(() => {
    if (enable) return;
    if (!subcategory) { setProducts([]); return; }
    getProducts(subcategory, brand || undefined).then(setProducts).catch(() => setProducts([]));
  }, [enable, subcategory, brand, refreshKey]);

  // keep the internal selects in sync when the product is set programmatically
  useEffect(() => {
    if (!value) return;
    if (value.subcategory && value.subcategory !== subcategory) {
      if (!isLocked) {
        const cat = categories.find((c) => (c.subcategories || []).some((s) => s.id === value.subcategory))?.id;
        if (cat) setCategory(cat);
      }
      setSubcategory(value.subcategory);
    }
    if (value.brand && value.brand !== brand) setBrand(value.brand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id]);

  const defs = enable ? FILTER_DEFS[category] : null;
  const thicknessNum = num(thickness);

  // Products shown in the selector (filtered in real-time when enable)
  const filtered = useMemo(() => {
    if (!enable) return products;
    let list = catProducts.slice();
    if (subcategory) list = list.filter((p) => p.subcategory === subcategory);
    if (brand) list = list.filter((p) => (p.brand || "").toLowerCase() === brand.toLowerCase());
    if (defs) {
      const activeToggles = defs.toggles.filter((d) => active[d.key]);
      list = list.filter((p) => {
        const s = p.specs || {};
        for (const d of activeToggles) if (!d.match(s)) return false;
        if (defs.thickness && thicknessNum > 0 && s.spessore_max_mm != null && num(s.spessore_max_mm) < thicknessNum) return false;
        return true;
      });
    }
    return list;
  }, [enable, products, catProducts, subcategory, brand, defs, active, thicknessNum]);

  const brandOptions = useMemo(() => {
    if (!enable) return brands;
    const pool = subcategory ? catProducts.filter((p) => p.subcategory === subcategory) : catProducts;
    const uniq = {};
    for (const p of pool) {
      const b = p.brand;
      if (b && !uniq[b.toLowerCase()]) uniq[b.toLowerCase()] = b;
    }
    return Object.values(uniq).sort();
  }, [enable, brands, catProducts, subcategory]);

  const toggleFilter = (key) => setActive((a) => ({ ...a, [key]: !a[key] }));

  // Equivalent-match: rank the competitor brand's products by similarity to the reference product A
  useEffect(() => {
    if (enable || !refProductId || !brand || !subcategory) {
      setSuggestedIds(new Set()); setOrderIds([]); onSuggested?.(new Set(), []); return;
    }
    let alive = true;
    getSimilarList(refProductId, brand)
      .then((d) => {
        if (!alive) return;
        const sug = d.suggestions || [];
        const ids = new Set(sug.map((s) => s.id));
        setSuggestedIds(ids); setOrderIds(d.order || []);
        onSuggested?.(ids, sug);
      })
      .catch(() => { if (alive) { setSuggestedIds(new Set()); setOrderIds([]); onSuggested?.(new Set(), []); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enable, refProductId, brand, subcategory, products.length]);

  const displayList = useMemo(() => {
    if (!suggestedIds.size) return filtered;
    const sug = [], rest = [];
    for (const p of filtered) (suggestedIds.has(p.id) ? sug : rest).push(p);
    const rank = (p) => { const i = orderIds.indexOf(p.id); return i < 0 ? 1e9 : i; };
    sug.sort((a, b) => rank(a) - rank(b));
    rest.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return [...sug, ...rest];
  }, [filtered, suggestedIds, orderIds]);

  const clearFilters = () => { setActive({}); setThickness(""); };
  const hasActiveFilters = Object.values(active).some(Boolean) || thicknessNum > 0;

  const accent = side === "a" ? "border-l-info" : "border-l-primary";
  const productDisabled = enable ? !category : !subcategory;

  return (
    <div
      data-testid={`product-picker-${side}`}
      className={`border border-border border-l-4 ${accent} bg-card`}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <span className="label-caps text-muted-foreground">
          {side === "a" ? t("productA") : side === "b" ? t("productB") : t("product")}
        </span>
        {value ? (
          <span className="font-mono text-xs text-foreground truncate max-w-[55%]">
            {value.brand} · {value.name}
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
        <Field label={t("category")} icon={Layers}>
          <Select value={category} disabled={isLocked} onValueChange={(v) => { setCategory(v); setSubcategory(""); setBrand(""); clearFilters(); onSelect(null); }}>
            <SelectTrigger data-testid={`select-category-${side}`} className={selCls}>
              <SelectValue placeholder={t("selectPlaceholder")} />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id} className="font-mono text-sm">{tc(c.id)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t("subcategory")} icon={FolderTree}>
          <Select value={subcategory} disabled={!category} onValueChange={(v) => { setSubcategory(v); setBrand(""); if (!enable) onSelect(null); }}>
            <SelectTrigger data-testid={`select-subcategory-${side}`} className={selCls}>
              <SelectValue placeholder={t("selectPlaceholder")} />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {subs.map((s) => (
                <SelectItem key={s.id} value={s.id} className="font-mono text-sm">{tc(s.id)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t("brand")} icon={Tag}>
          <Select value={brand} disabled={enable ? !category : !subcategory} onValueChange={(v) => { setBrand(v); if (!enable) onSelect(null); }}>
            <SelectTrigger data-testid={`select-brand-${side}`} className={selCls}>
              <SelectValue placeholder={t("selectPlaceholder")} />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {brandOptions.map((b) => (
                <SelectItem key={b} value={b} className="font-mono text-sm">{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t("product")} icon={Package}>
          <Select
            value={value?.id || ""}
            disabled={productDisabled}
            onValueChange={(v) => onSelect(filtered.find((p) => p.id === v) || null)}
          >
            <SelectTrigger data-testid={`select-product-${side}`} className={selCls}>
              <SelectValue placeholder={t("selectPlaceholder")} />
            </SelectTrigger>
            <SelectContent className="rounded-none max-h-72">
              {displayList.map((p, i) => {
                const isSug = suggestedIds.has(p.id);
                const isBest = isSug && orderIds.indexOf(p.id) === 0;
                return (
                  <SelectItem key={p.id} value={p.id} className="font-mono text-sm">
                    <span className="flex w-full items-center justify-between gap-2">
                      <span className="truncate">{p.name}</span>
                      {isSug && (
                        <span className={`ml-2 inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${isBest ? "bg-primary text-primary-foreground" : "bg-info/15 text-info"}`}>
                          <Sparkles className="h-2.5 w-2.5" />{isBest ? t("bestMatch") : t("techEquivalent")}
                        </span>
                      )}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {/* Dynamic technical filters — only in "Scelta Materiale" (enableFilters) */}
      {enable && category && defs && (
        <div className="border-t border-border bg-surface px-4 py-3" data-testid="quick-filters">
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 label-caps text-muted-foreground">
              <SlidersHorizontal className="h-3.5 w-3.5 text-primary" /> {t("filtersTitle")}
            </span>
            {hasActiveFilters && (
              <button data-testid="clear-filters" onClick={clearFilters} className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-destructive">
                <X className="h-3 w-3" /> {t("clearFilters")}
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {defs.toggles.map((d) => (
              <button
                key={d.key}
                data-testid={`filter-chip-${d.key}`}
                onClick={() => toggleFilter(d.key)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active[d.key]
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground hover:border-primary/60"
                }`}
              >
                {t(d.labelKey)}
              </button>
            ))}
            {defs.thickness && (
              <div className="flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1">
                <span className="text-[11px] font-semibold text-muted-foreground">{t("fThickness")}</span>
                <input
                  data-testid="filter-thickness"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  placeholder="—"
                  className="w-14 bg-transparent text-center font-mono text-xs outline-none"
                />
              </div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid="filter-count">
            {filtered.length} {t("fResults")}
          </p>
          {filtered.length === 0 && (
            <p data-testid="no-match" className="mt-1 border border-dashed border-border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground">
              {t("noMatch")}
            </p>
          )}
        </div>
      )}

      <button
        data-testid={`upload-trigger-${side}`}
        onClick={() => onOpenUpload(subcategory || "colle_piastrelle")}
        className="group flex w-full items-center gap-3 border-t-2 border-dashed border-primary/60 bg-primary/10 px-4 py-3 text-left transition-colors hover:bg-primary/20"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-primary text-primary-foreground">
          <Upload className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="text-sm font-bold text-foreground">{t("addMissing")}</span>
          <span className="text-[11px] text-muted-foreground">{t("addMissingHint")}</span>
        </span>
        <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
      </button>
      {isLocked && lockCategory && (
        <div className="border-t border-border bg-info/10 px-4 py-2 text-[11px] text-muted-foreground">
          {t("lockInfo")}
        </div>
      )}
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5 label-caps text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </span>
      {children}
    </div>
  );
}
